import {
  Box,
  Chip,
  CloseIcon,
  Divider,
  GKButton,
  GKEditor,
  GKSearchSelect,
  Grid2,
  IconButton,
  InfolconIcon,
  Tooltip,
  Typography,
} from "components";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

interface EmailConfigFormValues {
  introduction: string;
  footer: string;
  ccEmail: string[];
  bccEmail: string[];
}

const MAX_CC_EMAILS = 5;
const MAX_BCC_EMAILS = 5;

const validateEmail = (email: string) =>
  /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(String(email).toLowerCase());

interface Props {
  data: {
    company: number;
    dailyEmail: string;
    email: string;
    footer: string;
    host: string;
    id: string;
    introduction: string;
    isConnection: false;
    isDefault: boolean;
    password: string;
    senderName: string;
    useTls: string;
    ccEmail: string[];
    bccEmail: string[];
  };
  handleUpdate: any;
  isLoading: boolean;
}

const EmailHeaderSection = (props: Props) => {
  const { data, handleUpdate, isLoading } = props;

  const parseEmailArray = (value: any): string[] => {
    if (Array.isArray(value)) return value;
    if (typeof value !== "string" || !value) return [];
    return value.match(/[\w-.]+@([\w-]+\.)+[\w-]{2,4}/g) || [];
  };

  const form = useForm<EmailConfigFormValues>({
    defaultValues: {
      introduction: data?.introduction || "",
      footer: data?.footer || "",
      ccEmail: [],
      bccEmail: [],
    },
    values: {
      introduction: data?.introduction || "",
      footer: data?.footer || "",
      ccEmail: parseEmailArray(data?.ccEmail),
      bccEmail: parseEmailArray(data?.bccEmail),
    },
  });

  const onSubmit: SubmitHandler<EmailConfigFormValues> = (values) => {
    handleUpdate(values);
  };

  return (
    <form id="email-header-config-form" onSubmit={form.handleSubmit(onSubmit)}>
      <Grid2 container spacing={2}>
        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Email Footer
          </Typography>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={12}>
            <Controller
              name="footer"
              control={form.control}
              render={({ field }) => (
                <GKEditor
                  value={field.value}
                  onChange={field.onChange}
                  inputLabel="The content you enter here will be present at the end of all emails sent to the clients."
                  toolbar={[
                    "undo",
                    "redo",
                    "|",
                    "heading",
                    "bold",
                    "italic",
                    "underline",
                    "strikethrough",
                    "blockQuote",
                    "|",
                    "fontSize",
                    "fontColor",
                    "fontBackgroundColor",
                    "|",
                    "alignment",
                    "subscript",
                    "superscript",
                    "link",
                    "code",
                    "|",
                    "bulletedList",
                    "numberedList",
                  ]}
                />
              )}
            />
          </Grid2>
        </Grid2>

        <Grid2 size={12} my={1}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Signature
          </Typography>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={12}>
            <Controller
              name="introduction"
              control={form.control}
              render={({ field }) => (
                <GKEditor
                  {...field}
                  onChange={field.onChange}
                  inputLabel="This signature will appear at the end of every email sent to the clients."
                  toolbar={[
                    "undo",
                    "redo",
                    "|",
                    "heading",
                    "bold",
                    "italic",
                    "underline",
                    "strikethrough",
                    "blockQuote",
                    "|",
                    "fontSize",
                    "fontColor",
                    "fontBackgroundColor",
                    "|",
                    "alignment",
                    "subscript",
                    "superscript",
                    "link",
                    "code",
                    "|",
                    "bulletedList",
                    "numberedList",
                  ]}
                />
              )}
            />
          </Grid2>
        </Grid2>

        <Grid2 size={12} my={1}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              fontSize={16}
              fontWeight={500}
              color={"var(--neutral-500)"}
            >
              CC Email Configuration
            </Typography>
            <IconButton>
              <Tooltip
                arrow
                title="You can write your email and press enter to add more email addresses."
                placement="right"
              >
                <InfolconIcon width={16} height={16} />
              </Tooltip>
            </IconButton>
          </Box>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={12}>
            <Controller
              name="ccEmail"
              control={form.control}
              rules={{
                validate: (value) => {
                  if (value.length > MAX_CC_EMAILS) {
                    return `You can add a maximum of ${MAX_CC_EMAILS} email addresses.`;
                  }
                  if (value.some((email) => !validateEmail(email))) {
                    return "Please enter a valid email address.";
                  }
                  return true;
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  requiredField
                  sx={{ width: "100%" }}
                  inputLabel=""
                  freeSolo
                  multiple
                  options={[]}
                  onChange={(_, val) => field.onChange(val)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        deleteIcon={<CloseIcon width={12} />}
                      />
                    ))
                  }
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
            {(!form.watch("ccEmail") || form.watch("ccEmail").length === 0) && (
              <Typography fontSize={12} color="#98A4b3">
                You can add maximum {MAX_CC_EMAILS} email addresses in CC.
              </Typography>
            )}
          </Grid2>
        </Grid2>

        <Grid2 size={12} my={1}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              fontSize={16}
              fontWeight={500}
              color={"var(--neutral-500)"}
            >
              BCC Email Configuration
            </Typography>
            <IconButton>
              <Tooltip
                arrow
                title="You can write your email and press enter to add more email addresses."
                placement="right"
              >
                <InfolconIcon width={16} height={16} />
              </Tooltip>
            </IconButton>
          </Box>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={12}>
            <Controller
              name="bccEmail"
              control={form.control}
              rules={{
                validate: (value) => {
                  if (value.length > MAX_BCC_EMAILS) {
                    return `You can add a maximum of ${MAX_BCC_EMAILS} email addresses.`;
                  }
                  if (value.some((email) => !validateEmail(email))) {
                    return "Please enter a valid email address.";
                  }
                  return true;
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  requiredField
                  sx={{ width: "100%" }}
                  inputLabel=""
                  freeSolo
                  multiple
                  options={[]}
                  onChange={(_, val) => field.onChange(val)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        deleteIcon={<CloseIcon width={12} />}
                      />
                    ))
                  }
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
            {(!form.watch("bccEmail") ||
              form.watch("bccEmail").length === 0) && (
              <Typography fontSize={12} color="#98A4b3">
                You can add maximum {MAX_BCC_EMAILS} email addresses in BCC.
              </Typography>
            )}
          </Grid2>
        </Grid2>

        <Grid2 size={12} my={1}>
          <Divider />
        </Grid2>

        <Grid2 size={12} display={"flex"} justifyContent={"flex-end"} mt={1}>
          <GKButton
            type="submit"
            variant="contained"
            form="email-header-config-form"
            loading={isLoading}
            onClick={(e) => e.stopPropagation()}
          >
            Save
          </GKButton>
        </Grid2>
      </Grid2>
    </form>
  );
};

export default EmailHeaderSection;
